import type { ReactNode } from 'react';
import styles from './TableOrCards.module.css';

export interface Column<R> {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  sticky?: boolean;
  width?: string;
  render?: (row: R) => ReactNode;
}

interface Props<R> {
  columns: Column<R>[];
  rows: R[];
  rowKey: (row: R) => string;
  cardTitle: (row: R) => ReactNode;
  cardMeta?: (row: R) => ReactNode;
  cardBody?: (row: R) => ReactNode;
  ariaLabel?: string;
  className?: string;
}

export function TableOrCards<R>({
  columns,
  rows,
  rowKey,
  cardTitle,
  cardMeta,
  cardBody,
  ariaLabel,
  className,
}: Props<R>) {
  return (
    <>
      <div className={[styles.tableWrap, className].filter(Boolean).join(' ')}>
        <table className={styles.table} aria-label={ariaLabel}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={[
                    c.align === 'right' ? styles.alignRight : '',
                    c.align === 'center' ? styles.alignCenter : '',
                    c.sticky ? styles.sticky : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={c.width ? { width: c.width } : undefined}
                  scope="col"
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={rowKey(row)}>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={[
                      c.align === 'right' ? styles.alignRight : '',
                      c.align === 'center' ? styles.alignCenter : '',
                      c.sticky ? styles.sticky : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {c.render ? c.render(row) : (row as Record<string, unknown>)[c.key] as ReactNode}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className={[styles.cards, className].filter(Boolean).join(' ')} aria-label={ariaLabel}>
        {rows.map((row) => (
          <li key={rowKey(row)} className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>{cardTitle(row)}</div>
              {cardMeta && <div className={styles.cardMeta}>{cardMeta(row)}</div>}
            </div>
            <dl className={styles.cardRows}>
              {columns.map((c) => (
                <div key={c.key} className={styles.cardRow}>
                  <dt>{c.header}</dt>
                  <dd>
                    {c.render ? c.render(row) : (row as Record<string, unknown>)[c.key] as ReactNode}
                  </dd>
                </div>
              ))}
            </dl>
            {cardBody && <div className={styles.cardBody}>{cardBody(row)}</div>}
          </li>
        ))}
      </ul>
    </>
  );
}
